import React, { useState, useEffect, useCallback, useRef } from 'react';
import { createPortal } from 'react-dom';
import { useNavigate, Link, useLocation } from 'react-router-dom';
import { useSettings } from '../contexts/SettingsContext';
import { useTheme } from '../contexts/ThemeContext';
import Popover, { PopoverMenuItem, PopoverDivider, PopoverFooter } from './Popover';
import { clearAuthSession, getMetadata } from '../utils/encryptedStorage';
import aiAutomationApi from '../utils/aiAutomationApi';
import OpenAIKeyWarning from './OpenAIKeyWarning';
import NotificationSummaryList from './notifications/NotificationSummaryList';
import { getNotificationGroupNavigationPath, getNotificationTotalCount } from '../utils/notificationUtils';

/**
 * UpcomingInterviewsModal - Shows all upcoming interviews
 */
const UpcomingInterviewsModal = ({ isOpen, onClose, primaryColor, companyId = '', userId = '', searchId = '', candidateEmail = '' }) => {
  const { isDark } = useTheme();
  const [interviews, setInterviews] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [selectedInterviewForNotes, setSelectedInterviewForNotes] = useState(null);
  const [notesModalOpen, setNotesModalOpen] = useState(false);
  const [notesData, setNotesData] = useState(null);
  const [notesLoading, setNotesLoading] = useState(false);
  const [joinMeetLoading, setJoinMeetLoading] = useState({});
  const [candidateDetails, setCandidateDetails] = useState(null);
  const [candidateSkills, setCandidateSkills] = useState([]);
  const [aiAnalysis, setAiAnalysis] = useState(null);
  const [analysisLoading, setAnalysisLoading] = useState(false);
  const [regenerateLoading, setRegenerateLoading] = useState(false);
  const [showConversation, setShowConversation] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');

  const filteredInterviews = interviews.filter((iv) => {
    if (!searchQuery.trim()) return true;
    const q = searchQuery.toLowerCase();
    return (
      (iv.candidate_name || '').toLowerCase().includes(q) ||
      (iv.candidate_email || '').toLowerCase().includes(q) ||
      (iv.interview_title || iv.job_title || '').toLowerCase().includes(q) ||
      (iv.interviewer_name || '').toLowerCase().includes(q)
    );
  });

  const fetchInterviews = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const query = new URLSearchParams();
      if (companyId) query.set('cid', String(companyId));
      if (userId) query.set('user_id', String(userId));
      if (searchId) query.set('search_id', String(searchId));
      if (candidateEmail) query.set('candidate_email', String(candidateEmail));
      const data = await aiAutomationApi.get(`/interviews/calendar/all${query.toString() ? `?${query.toString()}` : ''}`);
      if (data?.interviews) {
        // Sort by date and time, show only upcoming
        const now = new Date();
        const upcoming = data.interviews
          .filter((iv) => {
            if (!iv.scheduled_date) return false;
            const ivDate = new Date(iv.scheduled_date);
            return ivDate >= new Date(now.getFullYear(), now.getMonth(), now.getDate());
          })
          .sort((a, b) => {
            const dateCompare = (a.scheduled_date || '').localeCompare(b.scheduled_date || '');
            if (dateCompare !== 0) return dateCompare;
            return (a.scheduled_time || '').localeCompare(b.scheduled_time || '');
          });
        setInterviews(upcoming);
      }
    } catch (err) {
      console.error('Failed to fetch interviews:', err);
      setError('Failed to load interviews');
    } finally {
      setLoading(false);
    }
  }, [companyId, userId, searchId, candidateEmail]);

  useEffect(() => {
    if (isOpen) {
      fetchInterviews();
    }
  }, [isOpen, fetchInterviews]);

  const handleJoinMeet = async (interview) => {
    const interviewId = interview.id;
    setJoinMeetLoading(prev => ({ ...prev, [interviewId]: true }));
    try {
      console.log('[AppHeader] Join Meet clicked', { interview_id: interviewId, google_meet_link: interview.google_meet_link, interview_title: interview.interview_title });
      console.log('[AppHeader] ====== CALLING BACKEND ENDPOINT ======');
      console.log('[AppHeader] Endpoint:', `/interviews/${interviewId}/join-meeting`);
      console.log('[AppHeader] Full URL will be:', `${process.env.REACT_APP_AI_AUTOMATION_URL || 'http://localhost:8100'}/api/v1/interviews/${interviewId}/join-meeting`);
      
      const response = await aiAutomationApi.post(`/interviews/${interviewId}/join-meeting`, {});
      
      console.log('[AppHeader] ====== BACKEND RESPONSE RECEIVED ======');
      console.log('[AppHeader] Response type:', typeof response);
      console.log('[AppHeader] Response data:', response);
      console.log('[AppHeader] Response keys:', Object.keys(response));
      
      if (response.success) {
        // Open the Google Meet link in a new tab
        if (interview.google_meet_link) {
          window.open(interview.google_meet_link, '_blank');
        }
        alert(`AI Note Taker bot has been requested to join the meeting!\nMeeting ID: ${response.meeting_id}\nBot ID: ${response.bot_id}`);
        // Refresh interviews to get updated status
        await fetchInterviews();
      } else {
        alert(`Failed to join meeting: ${response.message || response.error || 'Unknown error'}`);
      }
    } catch (err) {
      console.error('[AppHeader] Failed to join meeting:', err);
      alert(`Failed to join meeting: ${err.message || 'Unknown error'}`);
    } finally {
      setJoinMeetLoading(prev => {
        const next = { ...prev };
        delete next[interviewId];
        return next;
      });
    }
  };

  const fetchCandidateDetailsAndSkills = async (interviewId) => {
    try {
      console.log('[AppHeader] Fetching candidate details and skills for:', interviewId);
      const data = await aiAutomationApi.get(`/interviews/${interviewId}/candidate-info`);
      console.log('[AppHeader] Candidate info response:', data);
      if (data.success) {
        setCandidateDetails(data);
        setCandidateSkills(data.skills || []);
      }
    } catch (err) {
      console.error('[AppHeader] Failed to fetch candidate info:', err);
    }
  };

  const handleAnalyzeWithAI = async (interviewId, force = false) => {
    if (!interviewId) return;
    setAnalysisLoading(true);
    try {
      console.log('[AppHeader] Analyzing interview with AI:', interviewId);
      const data = await aiAutomationApi.post(`/interviews/${interviewId}/notes/analyze`, force ? { force: true } : {});
      console.log('[AppHeader] AI Analysis response:', data);
      setAiAnalysis(data);
      
      // If notes are not ready, keep loading state
      if (data.status === 'notes_not_ready') {
        // Keep analysisLoading true to show loader
        return;
      }
    } catch (err) {
      console.error('[AppHeader] Failed to analyze interview:', err);
      setAiAnalysis({ success: false, error: err.message || 'Failed to generate AI analysis' });
    } finally {
      setAnalysisLoading(false);
    }
  };

  const handleRegenerateNotes = async (interviewId) => {
    if (!interviewId) return;
    setRegenerateLoading(true);
    setNotesLoading(true);
    setAnalysisLoading(false);
    setAiAnalysis(null);
    try {
      console.log('[AppHeader] Regenerating AI notes for interview:', interviewId);
      const data = await aiAutomationApi.post(`/interviews/${interviewId}/notes/regenerate`, {});
      console.log('[AppHeader] Regenerated notes response:', data);
      setNotesData(data);
      await fetchCandidateDetailsAndSkills(interviewId);
      await handleAnalyzeWithAI(interviewId, true);
    } catch (err) {
      console.error('[AppHeader] Failed to regenerate notes:', err);
      setNotesData({ success: false, error: err.message || 'Could not regenerate notes from AI Note Taker' });
    } finally {
      setRegenerateLoading(false);
      setNotesLoading(false);
    }
  };

  const handleViewNotes = async (interview) => {
    setSelectedInterviewForNotes(interview);
    setNotesModalOpen(true);
    setNotesLoading(true);
    setAnalysisLoading(false);
    setAiAnalysis(null);
    setCandidateDetails(null);
    setCandidateSkills([]);
    setShowConversation(false);
    
    try {
      console.log('[AppHeader] View Notes clicked', { interview_id: interview.id });
      console.log('[AppHeader] Calling backend notes endpoint:', `/interviews/${interview.id}/notes`);
      const data = await aiAutomationApi.get(`/interviews/${interview.id}/notes`);
      console.log('[AppHeader] Notes response:', data);
      setNotesData(data);
      setAiAnalysis(data?.meeting_analysis_json?.success ? data.meeting_analysis_json : null);
      
      // Fetch candidate details and skills
      await fetchCandidateDetailsAndSkills(interview.id);
      
      // Only call OpenAI if cached analysis is not already available.
      if (!data?.meeting_analysis_json?.success) {
        console.log('[AppHeader] Auto-triggering AI analysis for interview:', interview.id);
        await handleAnalyzeWithAI(interview.id);
      }
    } catch (err) {
      console.error('[AppHeader] Failed to load notes:', err);
      setNotesData({ success: false, error: err.message || 'Could not fetch notes from AI Note Taker' });
    } finally {
      setNotesLoading(false);
    }
  };

  if (!isOpen && !notesModalOpen) return null;

  const formatTime = (time) => {
    if (!time) return '';
    const [hours, minutes] = time.split(':');
    const h = parseInt(hours, 10);
    const ampm = h >= 12 ? 'PM' : 'AM';
    const hour12 = h % 12 || 12;
    return `${hour12}:${minutes} ${ampm}`;
  };

  const isInterviewCompleted = (interview) => {
    // Completion flags from backend should override stale in-progress status strings.
    if (interview?.meeting_completed || interview?.notes_cached_at) {
      return true;
    }

    const noteTakerStatus = String(interview?.ai_note_taker_status || '').toLowerCase();
    if (['recording', 'active', 'joined', 'joining', 'in_progress', 'bot_requested', 'awaiting_admission', 'pending'].includes(noteTakerStatus)) {
      return false;
    }

    if (['completed', 'recording_completed', 'ended', 'left', 'done', 'finished', 'disconnected', 'closed'].includes(noteTakerStatus)) {
      return true;
    }

    const status = String(interview?.status || '').toLowerCase();
    if (status === 'completed') return true;

    const dateValue = String(interview?.scheduled_date || '').trim();
    if (!dateValue) return false;

    const timeValue = String(interview?.scheduled_time || '').trim() || '00:00:00';
    const scheduledAt = new Date(`${dateValue}T${timeValue}`);
    if (Number.isNaN(scheduledAt.getTime())) return false;

    return scheduledAt <= new Date();
  };

  const getInterviewStatusMeta = (interview) => {
    const rawStatus = String(interview?.ai_note_taker_status || '').trim().toLowerCase();

    if (isInterviewCompleted(interview)) {
      return {
        label: interview?.notes_cached_at ? 'Completed' : 'Ended',
        className: 'bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-300',
      };
    }

    if (['recording', 'active', 'joined', 'joining', 'in_progress'].includes(rawStatus)) {
      return {
        label: 'In Progress',
        className: 'bg-blue-100 text-blue-800 dark:bg-blue-900/30 dark:text-blue-300',
      };
    }

    if (rawStatus === 'awaiting_admission') {
      return {
        label: 'Awaiting Admit',
        className: 'bg-amber-100 text-amber-800 dark:bg-amber-900/30 dark:text-amber-300',
      };
    }

    if (rawStatus === 'bot_requested') {
      return {
        label: 'Bot Requested',
        className: 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900/30 dark:text-yellow-300',
      };
    }

    if (['error', 'failed'].includes(rawStatus)) {
      return {
        label: 'Failed',
        className: 'bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-300',
      };
    }

    return {
      label: String(interview?.status || 'scheduled').toLowerCase() === 'completed' ? 'Scheduled' : 'Upcoming',
      className: 'bg-gray-100 text-gray-800 dark:bg-gray-700 dark:text-gray-300',
    };
  };

  const canViewInterviewNotes = (interview) => {
    return Boolean(
      interview?.ai_note_taker_meeting_id &&
      (interview?.meeting_completed || interview?.notes_cached_at || isInterviewCompleted(interview))
    );
  };

  return (
    <>
      {isOpen && createPortal(
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      {/* Backdrop */}
      <div
        className="absolute inset-0 bg-black/50 backdrop-blur-sm"
        onClick={onClose}
      />
      
      {/* Modal - Increased to max-w-6xl for better table display */}
      <div className={`relative w-full max-w-6xl max-h-[90vh] overflow-hidden rounded-xl shadow-2xl ${isDark ? 'bg-gray-800' : 'bg-white'}`}>
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-gray-200 dark:border-gray-700">
          <div className="flex items-center gap-3">
            <div
              className="w-10 h-10 rounded-lg flex items-center justify-center"
              style={{ backgroundColor: `${primaryColor}20` }}
            >
              <svg className="w-5 h-5" fill="none" stroke={primaryColor} viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
              </svg>
            </div>
            <div>
              <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
                Upcoming Interviews
              </h3>
              <p className="text-sm text-gray-500 dark:text-gray-400">
                {filteredInterviews.length} upcoming interview{filteredInterviews.length !== 1 ? 's' : ''}{searchQuery && ` (filtered from ${interviews.length})`}
              </p>
            </div>
          </div>
          <div className="flex items-center gap-3">
            <div className="relative">
              <svg className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
              </svg>
              <input
                type="text"
                placeholder="Search interviews..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className={`pl-9 pr-3 py-2 text-sm rounded-lg border focus:outline-none focus:ring-2 transition-colors w-64 ${
                  isDark
                    ? 'bg-gray-700 border-gray-600 text-white placeholder-gray-400 focus:ring-blue-500'
                    : 'bg-gray-50 border-gray-300 text-gray-900 placeholder-gray-500 focus:ring-blue-500'
                }`}
              />
            </div>
            <button
              onClick={onClose}
              className="p-2 rounded-lg text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors"
            >
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>
        </div>

        {/* Content - Increased max-height for more content display */}
        <div className="px-6 py-4 overflow-y-auto max-h-[75vh]">
          {loading ? (
            <div className="flex items-center justify-center py-12">
              <div className="animate-spin rounded-full h-8 w-8 border-2 border-gray-300 dark:border-gray-600 border-t-transparent" style={{ borderTopColor: primaryColor }} />
            </div>
          ) : error ? (
            <div className="text-center py-8 text-red-500">{error}</div>
          ) : filteredInterviews.length === 0 ? (
            <div className="text-center py-12">
              <svg className="w-16 h-16 mx-auto mb-4 text-gray-300 dark:text-gray-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
              </svg>
              <p className="text-gray-500 dark:text-gray-400">{searchQuery ? 'No interviews match your search.' : 'No upcoming interviews scheduled.'}</p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm min-w-[900px]">
                <thead>
                  <tr className="border-b border-gray-200 dark:border-gray-700">
                    <th className="text-left py-3 px-4 font-semibold text-gray-700 dark:text-gray-300 whitespace-nowrap">Date</th>
                    <th className="text-left py-3 px-4 font-semibold text-gray-700 dark:text-gray-300 whitespace-nowrap">Time</th>
                    <th className="text-left py-3 px-4 font-semibold text-gray-700 dark:text-gray-300 whitespace-nowrap">Candidate</th>
                    <th className="text-left py-3 px-4 font-semibold text-gray-700 dark:text-gray-300 whitespace-nowrap">Job Title</th>
                    <th className="text-left py-3 px-4 font-semibold text-gray-700 dark:text-gray-300 whitespace-nowrap">Interviewer</th>
                    <th className="text-left py-3 px-4 font-semibold text-gray-700 dark:text-gray-300 whitespace-nowrap">Status</th>
                    <th className="text-center py-3 px-4 font-semibold text-gray-700 dark:text-gray-300 whitespace-nowrap">Action</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredInterviews.map((interview, idx) => {
                    const statusMeta = getInterviewStatusMeta(interview);
                    const canViewNotes = canViewInterviewNotes(interview);
                    const shouldDisableJoin = joinMeetLoading[interview.id] || canViewNotes || isInterviewCompleted(interview);
                    return (
                    <tr key={interview.id || idx} className="border-b border-gray-100 dark:border-gray-700 hover:bg-gray-50 dark:hover:bg-gray-700/50 transition-colors">
                      <td className="py-3 px-4 text-gray-900 dark:text-white whitespace-nowrap">
                        {interview.scheduled_date ? new Date(interview.scheduled_date).toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric', year: 'numeric' }) : '-'}
                      </td>
                      <td className="py-3 px-4 text-gray-900 dark:text-white whitespace-nowrap">
                        {formatTime(interview.scheduled_time)}
                      </td>
                      <td className="py-3 px-4">
                        <div className="text-gray-900 dark:text-white font-medium">
                          {interview.candidate_name || 'N/A'}
                        </div>
                        <div className="text-gray-500 dark:text-gray-400 text-xs">
                          {interview.candidate_email || ''}
                        </div>
                      </td>
                      <td className="py-3 px-4 text-gray-900 dark:text-white">
                        {interview.interview_title || interview.job_title || '-'}
                      </td>
                      <td className="py-3 px-4 text-gray-900 dark:text-white">
                        {interview.interviewer_name || '-'}
                      </td>
                      <td className="py-3 px-4">
                        <span className={`inline-flex items-center rounded-full px-2.5 py-1 text-xs font-semibold ${statusMeta.className}`}>
                          {statusMeta.label}
                        </span>
                      </td>
                      <td className="py-3 px-4 text-center">
                        <div className="flex items-center justify-center gap-2">
                          {interview.google_meet_link ? (
                            <>
                              <button
                                onClick={() => handleJoinMeet(interview)}
                                disabled={shouldDisableJoin}
                                className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium text-white rounded-lg hover:opacity-90 transition-opacity disabled:opacity-50 disabled:cursor-not-allowed"
                                style={{ backgroundColor: primaryColor }}
                              >
                                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 10l4.553-2.276A1 1 0 0121 8.618v6.764a1 1 0 01-1.447.894L15 14M5 18h8a2 2 0 002-2V8a2 2 0 00-2-2H5a2 2 0 00-2 2v8a2 2 0 002 2z" />
                                </svg>
                                {joinMeetLoading[interview.id] ? 'Joining...' : (shouldDisableJoin ? 'Meeting Ended' : 'Join Meet')}
                              </button>
                              {canViewNotes && (
                                <button
                                  onClick={() => handleViewNotes(interview)}
                                  className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium rounded-lg border transition-opacity hover:opacity-90"
                                  style={{ borderColor: primaryColor, color: primaryColor, backgroundColor: 'transparent' }}
                                >
                                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                                  </svg>
                                  Notes
                                </button>
                              )}
                            </>
                          ) : (
                            <span className="text-gray-400 dark:text-gray-500 text-xs">No link</span>
                          )}
                        </div>
                      </td>
                    </tr>
                  );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="flex justify-end px-6 py-4 border-t border-gray-200 dark:border-gray-700">
          <button
            onClick={onClose}
            className="px-4 py-2 text-sm font-medium text-gray-700 dark:text-gray-300 bg-gray-100 dark:bg-gray-700 rounded-lg hover:bg-gray-200 dark:hover:bg-gray-600 transition-colors"
          >
            Close
          </button>
        </div>
      </div>
    </div>,
    document.body
      )}
      {notesModalOpen && createPortal(
        <div className="fixed inset-0 z-[100] flex items-center justify-center bg-black/50 backdrop-blur-sm" onClick={() => setNotesModalOpen(false)}>
          <div className="bg-white dark:bg-gray-800 rounded-xl shadow-xl w-full max-w-7xl mx-4 max-h-[95vh] flex flex-col overflow-hidden" onClick={(e) => e.stopPropagation()}>
            <div className="px-6 py-4 border-b border-gray-200 dark:border-gray-700 flex items-center justify-between flex-shrink-0">
              <div>
                <h2 className="text-xl font-bold text-gray-900 dark:text-white">AI Meeting Notes</h2>
                {selectedInterviewForNotes && (
                  <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">
                    {selectedInterviewForNotes.candidate_name} ({selectedInterviewForNotes.candidate_email})
                  </p>
                )}
              </div>
              <div className="flex items-center gap-3">
                <button
                  onClick={() => selectedInterviewForNotes && handleRegenerateNotes(selectedInterviewForNotes.id)}
                  disabled={!selectedInterviewForNotes || notesLoading || analysisLoading || regenerateLoading}
                  className="px-4 py-2 text-sm font-medium text-white rounded-lg transition-opacity disabled:opacity-50 disabled:cursor-not-allowed"
                  style={{ backgroundColor: primaryColor }}
                >
                  {regenerateLoading ? 'Regenerating...' : 'Regenerate AI Note'}
                </button>
                <button onClick={() => setNotesModalOpen(false)} className="p-1 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors">
                  <svg className="w-5 h-5 text-gray-500" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" /></svg>
                </button>
              </div>
            </div>
            <div className="min-h-0 flex-1 overflow-y-auto px-6 py-4">
              {(notesLoading || analysisLoading) ? (
                <div className="text-center py-12">
                  <div className="inline-block animate-spin rounded-full h-16 w-16 border-4 border-gray-300 border-t-blue-600 mb-4"></div>
                  <p className="text-gray-600 dark:text-gray-400 text-lg font-medium">Generating AI analysis...</p>
                  <p className="text-gray-500 dark:text-gray-500 text-sm mt-2">Please wait while we analyze the interview</p>
                  {aiAnalysis?.status === 'notes_not_ready' && (
                    <div className="mt-4">
                      <p className="text-sm text-amber-600 dark:text-amber-400 mb-2">Waiting for meeting notes...</p>
                      <button
                        onClick={() => selectedInterviewForNotes && handleAnalyzeWithAI(selectedInterviewForNotes.id)}
                        className="px-4 py-2 text-sm rounded-lg text-white font-medium"
                        style={{ backgroundColor: primaryColor }}
                      >
                        Retry Analysis
                      </button>
                    </div>
                  )}
                </div>
              ) : !notesData ? (
                <div className="text-center py-8 text-gray-500">No notes data</div>
              ) : notesData.success === false ? (
                <div className="text-center py-8">
                  <p className="text-red-500 mb-2">{notesData.message || notesData.error || 'Failed to load notes'}</p>
                  <p className="text-sm text-gray-500">{notesData.status === 'unknown' ? 'Click "Join Meet" first to start the bot.' : 'Please try again later.'}</p>
                </div>
              ) : (
                <div className="space-y-6">
                  {/* Top Section: Skills Match & Status */}
                  {aiAnalysis?.success && (
                    <div className="bg-gradient-to-r from-blue-50 to-teal-50 dark:from-blue-900/20 dark:to-teal-900/20 rounded-lg p-5">
                      {/* Skills Matching Score Circle and Description */}
                      <div className="flex items-center gap-4 mb-4">
                        <div className="relative w-20 h-20 flex-shrink-0">
                          <svg className="transform -rotate-90 w-20 h-20">
                            <circle cx="40" cy="40" r="32" stroke="currentColor" strokeWidth="6" fill="none" className="text-gray-200 dark:text-gray-700" />
                            <circle
                              cx="40"
                              cy="40"
                              r="32"
                              stroke="currentColor"
                              strokeWidth="6"
                              fill="none"
                              className="text-blue-600 dark:text-blue-400"
                              strokeDasharray={`${2 * Math.PI * 32}`}
                              strokeDashoffset={`${2 * Math.PI * 32 * (1 - (aiAnalysis.skills_matching_score || 0) / 10)}`}
                            />
                          </svg>
                          <div className="absolute inset-0 flex items-center justify-center">
                            <span className="text-xl font-bold text-blue-700 dark:text-blue-300">{aiAnalysis.skills_matching_score?.toFixed(1) || '0'}</span>
                          </div>
                        </div>
                        <div className="flex-1">
                          <p className="text-sm font-semibold text-gray-700 dark:text-gray-300 mb-1">Skills Match</p>
                          {aiAnalysis.summary && (
                            <p className="text-sm text-gray-600 dark:text-gray-400 leading-relaxed">{aiAnalysis.summary}</p>
                          )}
                        </div>
                      </div>
                      {/* Recommendation Badge - On next line */}
                      <div className="flex items-center">
                        <span className={`px-4 py-2 rounded-lg text-sm font-semibold inline-block ${
                          aiAnalysis.recommendation === 'Hire' ? 'bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-300' :
                          aiAnalysis.recommendation === 'No Hire' ? 'bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-300' :
                          'bg-yellow-100 text-yellow-800 dark:bg-yellow-900/30 dark:text-yellow-300'
                        }`}>
                          {aiAnalysis.recommendation === 'Maybe' ? 'Further Evaluation Needed' : aiAnalysis.recommendation || 'Further Evaluation Needed'}
                        </span>
                      </div>
                    </div>
                  )}

                  {/* Candidate Details & Skills with Overall Rating in top-right */}
                  {candidateDetails && (
                    <div className="bg-gray-50 dark:bg-gray-700/50 rounded-lg p-5 relative">
                      {/* Overall Rating in top-right corner */}
                      {aiAnalysis?.success && (
                        <div className="absolute top-4 right-4 text-center">
                          <div className="relative w-16 h-16">
                            <svg className="transform -rotate-90 w-16 h-16">
                              <circle cx="32" cy="32" r="26" stroke="currentColor" strokeWidth="5" fill="none" className="text-gray-200 dark:text-gray-700" />
                              <circle
                                cx="32"
                                cy="32"
                                r="26"
                                stroke="currentColor"
                                strokeWidth="5"
                                fill="none"
                                className="text-teal-600 dark:text-teal-400"
                                strokeDasharray={`${2 * Math.PI * 26}`}
                                strokeDashoffset={`${2 * Math.PI * 26 * (1 - (aiAnalysis.overall_rating || 0) / 10)}`}
                              />
                            </svg>
                            <div className="absolute inset-0 flex items-center justify-center">
                              <span className="text-lg font-bold text-teal-700 dark:text-teal-300">{aiAnalysis.overall_rating?.toFixed(1) || '0'}</span>
                            </div>
                          </div>
                          <p className="text-xs font-medium text-gray-700 dark:text-gray-300 mt-1">Overall</p>
                        </div>
                      )}
                      <h4 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">Candidate Details</h4>
                      <div className="grid grid-cols-2 gap-4 text-sm mb-4">
                        <div><strong className="text-gray-700 dark:text-gray-300">Current Role:</strong> <span className="text-gray-600 dark:text-gray-400">{candidateDetails.current_role || 'N/A'}</span></div>
                        <div><strong className="text-gray-700 dark:text-gray-300">Current Company:</strong> <span className="text-gray-600 dark:text-gray-400">{candidateDetails.current_company || 'N/A'}</span></div>
                        <div><strong className="text-gray-700 dark:text-gray-300">Experience:</strong> <span className="text-gray-600 dark:text-gray-400">{candidateDetails.experience_years || 'N/A'} years</span></div>
                        <div><strong className="text-gray-700 dark:text-gray-300">Location:</strong> <span className="text-gray-600 dark:text-gray-400">{candidateDetails.location || 'N/A'}</span></div>
                      </div>
                      <div>
                        <strong className="text-gray-700 dark:text-gray-300 text-sm">Skills:</strong>
                        <div className="flex flex-wrap gap-2 mt-2">
                          {candidateSkills && candidateSkills.length > 0 ? candidateSkills.map((skill, i) => (
                            <span key={i} className="px-3 py-1 text-xs font-medium rounded-full bg-blue-100 dark:bg-blue-900/30 text-blue-800 dark:text-blue-300">
                              {skill}
                            </span>
                          )) : (
                            <span className="text-sm text-gray-500">No skills available</span>
                          )}
                        </div>
                      </div>
                    </div>
                  )}

                  {/* AI Analysis Content */}
                  {aiAnalysis?.success && (
                    <div className="space-y-6">
                      {/* Individual Skill Ratings (including Communication Skills) */}
                      {aiAnalysis.skill_ratings && Array.isArray(aiAnalysis.skill_ratings) && aiAnalysis.skill_ratings.length > 0 && (
                        <div className="bg-white dark:bg-gray-700/50 rounded-lg p-5 border border-gray-200 dark:border-gray-600">
                          <h4 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">Skills & Values ({aiAnalysis.skill_ratings.length})</h4>
                          <div className="space-y-4">
                            {aiAnalysis.skill_ratings.map((skillRating, idx) => {
                              const score = parseFloat(skillRating.score) || 0;
                              const percentage = (score / 10) * 100;
                              return (
                                <div key={idx} className="space-y-2">
                                  <div className="flex items-center justify-between">
                                    <span className="text-sm font-medium text-gray-700 dark:text-gray-300">{skillRating.skill}</span>
                                    <span className="text-sm font-semibold text-gray-900 dark:text-white">{score.toFixed(1)} / 10</span>
                                  </div>
                                  <div className="w-full bg-gray-200 dark:bg-gray-600 rounded-full h-2.5">
                                    <div
                                      className="bg-teal-500 h-2.5 rounded-full transition-all"
                                      style={{ width: `${percentage}%` }}
                                    ></div>
                                  </div>
                                  {skillRating.description && (
                                    <p className="text-xs text-gray-600 dark:text-gray-400">{skillRating.description}</p>
                                  )}
                                </div>
                              );
                            })}
                          </div>
                        </div>
                      )}

                      {/* Key Takeaways */}
                      {aiAnalysis.key_takeaways && Array.isArray(aiAnalysis.key_takeaways) && aiAnalysis.key_takeaways.length > 0 && (
                        <div className="bg-white dark:bg-gray-700/50 rounded-lg p-5 border border-gray-200 dark:border-gray-600">
                          <h4 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">Key Takeaways</h4>
                          <ul className="space-y-2">
                            {aiAnalysis.key_takeaways.map((takeaway, idx) => (
                              <li key={idx} className="text-sm text-gray-600 dark:text-gray-400 flex items-start">
                                <span className="text-blue-500 mr-2">•</span>
                                <span>{takeaway}</span>
                              </li>
                            ))}
                          </ul>
                        </div>
                      )}
                    </div>
                  )}

                  {/* Summary */}
                  {notesData.notes?.executive_summary && (
                    <div>
                      <h4 className="text-lg font-semibold text-gray-900 dark:text-white mb-3">Summary</h4>
                      <p className="text-sm text-gray-600 dark:text-gray-400 whitespace-pre-wrap bg-gray-50 dark:bg-gray-700/50 p-4 rounded-lg">
                        {notesData.notes.executive_summary}
                      </p>
                    </div>
                  )}

                  {/* Transcript */}
                  {notesData.notes?.transcript && (
                    <div>
                      <h4 className="text-lg font-semibold text-gray-900 dark:text-white mb-3">Transcript</h4>
                      <div className="text-sm text-gray-600 dark:text-gray-400 bg-gray-50 dark:bg-gray-700/50 p-4 rounded-lg max-h-60 overflow-y-auto">
                        <pre className="whitespace-pre-wrap font-sans">{notesData.notes.transcript}</pre>
                      </div>
                    </div>
                  )}

                  {/* Meeting Conversation Button */}
                  {notesData.notes?.speakers && Array.isArray(notesData.notes.speakers) && notesData.notes.speakers.length > 0 && (
                    <div>
                      <button
                        onClick={() => setShowConversation(!showConversation)}
                        className="w-full px-4 py-3 text-left bg-blue-50 dark:bg-blue-900/20 hover:bg-blue-100 dark:hover:bg-blue-900/30 rounded-lg transition-colors flex items-center justify-between"
                      >
                        <span className="font-semibold text-blue-700 dark:text-blue-300">Meeting Conversation</span>
                        <svg className={`w-5 h-5 text-blue-700 dark:text-blue-300 transition-transform ${showConversation ? 'rotate-180' : ''}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                        </svg>
                      </button>
                      {showConversation && (
                        <div className="mt-4 space-y-3">
                          {(() => {
                            // Build chronological conversation
                            const allMessages = [];
                            notesData.notes.speakers.forEach(speaker => {
                              if (speaker.contributions && Array.isArray(speaker.contributions)) {
                                speaker.contributions.forEach(contrib => {
                                  if (typeof contrib === 'string') {
                                    // Extract timestamp and text: [00:00] text or [00:00:00] text
                                    const match = contrib.match(/^\[(\d{1,2}):(\d{2})(?::(\d{2}))?\]\s*(.*)/);
                                    if (match) {
                                      const hours = match[3] ? parseInt(match[1]) : 0;
                                      const minutes = match[3] ? parseInt(match[2]) : parseInt(match[1]);
                                      const seconds = match[3] ? parseInt(match[3]) : parseInt(match[2]);
                                      const text = match[4] || contrib;
                                      const timestamp = hours * 3600 + minutes * 60 + seconds;
                                      allMessages.push({
                                        timestamp,
                                        speaker: speaker.name,
                                        role: speaker.role,
                                        text: text,
                                        timeStr: match[1] + ':' + match[2] + (match[3] ? ':' + match[3] : '')
                                      });
                                    } else {
                                      allMessages.push({
                                        timestamp: 999999,
                                        speaker: speaker.name,
                                        role: speaker.role,
                                        text: contrib,
                                        timeStr: ''
                                      });
                                    }
                                  }
                                });
                              }
                            });
                            allMessages.sort((a, b) => a.timestamp - b.timestamp);
                            
                            return allMessages.map((msg, idx) => (
                              <div key={idx} className="flex items-start gap-3 p-3 bg-white dark:bg-gray-700/30 rounded-lg">
                                <div className="w-8 h-8 rounded-full flex items-center justify-center text-xs font-bold text-white flex-shrink-0" style={{ backgroundColor: primaryColor }}>
                                  {msg.speaker[0]?.toUpperCase() || '?'}
                                </div>
                                <div className="flex-1 min-w-0">
                                  <div className="flex items-center gap-2 mb-1">
                                    <span className="text-sm font-semibold text-gray-900 dark:text-white">{msg.speaker}</span>
                                    {msg.role && <span className="text-xs text-gray-500 dark:text-gray-400">({msg.role})</span>}
                                    {msg.timeStr && <span className="text-xs text-gray-400 dark:text-gray-500">[{msg.timeStr}]</span>}
                                  </div>
                                  <p className="text-sm text-gray-700 dark:text-gray-300">{msg.text}</p>
                                </div>
                              </div>
                            ));
                          })()}
                        </div>
                      )}
                    </div>
                  )}

                  {/* Recording */}
                  {notesData.recording_url && (
                    <div>
                      <h4 className="text-lg font-semibold text-gray-900 dark:text-white mb-3">Recording</h4>
                      <audio controls className="w-full">
                        <source src={notesData.recording_url} />
                        Your browser does not support the audio tag.
                      </audio>
                    </div>
                  )}
                </div>
              )}
            </div>
          </div>
        </div>,
        document.body
      )}
    </>
  );
};

/**
 * AppHeader - Reusable top navigation bar
 * Used across all post-login pages
 */
const AppHeader = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { settings } = useSettings();
  const { isDark, toggleTheme } = useTheme();
  const [upcomingModalOpen, setUpcomingModalOpen] = useState(false);
  const [notifications, setNotifications] = useState([]);
  const [notifLoading, setNotifLoading] = useState(false);
  const [notifCount, setNotifCount] = useState(0);
  const notifIntervalRef = useRef(null);

  const metadata = getMetadata();
  const companyId = metadata?.cid || '';
  const currentUserId = metadata?.uid || metadata?.userId || metadata?.id || '';
  const queryParams = new URLSearchParams(location.search);
  const currentSearchId = queryParams.get('searchId') || '';
  const currentCandidateEmail = queryParams.get('candidate') || '';

  const primaryColor = settings.theme?.colors?.primary || '#3B5BDB';
  const appName = settings.app?.name || 'Wizbox';
  const appLogo = settings.app?.logo || '/config/logo.svg';

  // Fetch action notifications (reschedule requests + question replies)
  const fetchNotifications = useCallback(async () => {
    try {
      setNotifLoading(true);
      const metadata = getMetadata();
      const cid = metadata?.cid || '';
      const data = await aiAutomationApi.get(`/interviews/action-notifications${cid ? `?cid=${cid}` : ''}`);
      const nextNotifications = Array.isArray(data?.notifications) ? data.notifications : [];
      setNotifications(nextNotifications);
      setNotifCount(getNotificationTotalCount(data, nextNotifications));
    } catch (err) {
      console.error('Failed to fetch notifications:', err);
    } finally {
      setNotifLoading(false);
    }
  }, []);

  // Poll notifications — only when tab is visible, every 60 seconds
  // Also listen for 'notificationsChanged' custom event (fired after reply/sync)
  useEffect(() => {
    fetchNotifications();

    const startPolling = () => {
      if (notifIntervalRef.current) clearInterval(notifIntervalRef.current);
      notifIntervalRef.current = setInterval(fetchNotifications, 300000); // 5 minutes
    };

    const handleVisibility = () => {
      if (document.hidden) {
        clearInterval(notifIntervalRef.current);
        notifIntervalRef.current = null;
      } else {
        fetchNotifications(); // refresh immediately when tab becomes visible
        startPolling();
      }
    };

    // Listen for notification changes triggered by reply/sync in CandidateDetail
    const handleNotifChanged = () => {
      fetchNotifications();
    };

    startPolling();
    document.addEventListener('visibilitychange', handleVisibility);
    window.addEventListener('notificationsChanged', handleNotifChanged);
    return () => {
      clearInterval(notifIntervalRef.current);
      document.removeEventListener('visibilitychange', handleVisibility);
      window.removeEventListener('notificationsChanged', handleNotifChanged);
    };
  }, [fetchNotifications]);

  // Clear all notifications
  const handleClearAll = useCallback(async () => {
    try {
      const metadata = getMetadata();
      const cid = metadata?.cid || '';
      await aiAutomationApi.patch(`/interviews/notifications/clear-all${cid ? `?cid=${cid}` : ''}`);
      setNotifications([]);
      setNotifCount(0);
    } catch (err) {
      console.error('Failed to clear all notifications:', err);
    }
  }, []);

  const handleOpenNotificationGroup = useCallback((group, close) => {
    close?.();
    navigate(getNotificationGroupNavigationPath(group));
  }, [navigate]);

  // Get user info from localStorage (encrypted metadata or plain user)
  const getUserInfo = () => {
    try {
      // First try to get from encrypted metadata
      const metadata = getMetadata();
      if (metadata && metadata.name) {
        return {
          name: metadata.name || 'User',
          email: metadata.email || '',
          profile: metadata.profile || null
        };
      }
      
      // Fallback to plain user storage
      const userStr = localStorage.getItem('user');
      if (userStr) {
        const userData = JSON.parse(userStr);
        return {
          name: userData.name || 'User',
          email: userData.email || '',
          profile: userData.profile || null
        };
      }
    } catch (e) {
      console.error('Error parsing user info:', e);
    }
    return { name: 'User', email: '', profile: null };
  };

  const user = getUserInfo();
  const userInitial = user.name ? user.name.charAt(0).toUpperCase() : 'U';

  const handleSignOut = () => {
    clearAuthSession();
    navigate('/auth/login');
  };

  const handleNavigateToSettings = () => {
    navigate('/account/business-details');
  };

  const handleNavigateToDatabase = () => {
    navigate('/master-database'); // Update this route when master database page is implemented
  }

  const handleNavigateToCampaigns = () => {
    navigate(`/chat?new=true&t=${Date.now()}`);
  };

  // 9-dot grid icon
  const GridIcon = () => (
    <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 20 20">
      <circle cx="4" cy="4" r="1.5" />
      <circle cx="10" cy="4" r="1.5" />
      <circle cx="16" cy="4" r="1.5" />
      <circle cx="4" cy="10" r="1.5" />
      <circle cx="10" cy="10" r="1.5" />
      <circle cx="16" cy="10" r="1.5" />
      <circle cx="4" cy="16" r="1.5" />
      <circle cx="10" cy="16" r="1.5" />
      <circle cx="16" cy="16" r="1.5" />
    </svg>
  );

  // Notification bell icon
  const BellIcon = () => (
    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        strokeWidth={1.5}
        d="M15 17h5l-1.405-1.405A2.032 2.032 0 0118 14.158V11a6.002 6.002 0 00-4-5.659V5a2 2 0 10-4 0v.341C7.67 6.165 6 8.388 6 11v3.159c0 .538-.214 1.055-.595 1.436L4 17h5m6 0v1a3 3 0 11-6 0v-1m6 0H9"
      />
    </svg>
  );

  // Sun icon (light mode)
  const SunIcon = () => (
    <svg className="w-5 h-5 text-yellow-500" fill="currentColor" viewBox="0 0 20 20">
      <path
        fillRule="evenodd"
        d="M10 2a1 1 0 011 1v1a1 1 0 11-2 0V3a1 1 0 011-1zm4 8a4 4 0 11-8 0 4 4 0 018 0zm-.464 4.95l.707.707a1 1 0 001.414-1.414l-.707-.707a1 1 0 00-1.414 1.414zm2.12-10.607a1 1 0 010 1.414l-.706.707a1 1 0 11-1.414-1.414l.707-.707a1 1 0 011.414 0zM17 11a1 1 0 100-2h-1a1 1 0 100 2h1zm-7 4a1 1 0 011 1v1a1 1 0 11-2 0v-1a1 1 0 011-1zM5.05 6.464A1 1 0 106.465 5.05l-.708-.707a1 1 0 00-1.414 1.414l.707.707zm1.414 8.486l-.707.707a1 1 0 01-1.414-1.414l.707-.707a1 1 0 011.414 1.414zM4 11a1 1 0 100-2H3a1 1 0 000 2h1z"
        clipRule="evenodd"
      />
    </svg>
  );

  // Moon icon (dark mode)
  const MoonIcon = () => (
    <svg className="w-5 h-5 text-gray-500 dark:text-gray-400" fill="currentColor" viewBox="0 0 20 20">
      <path d="M17.293 13.293A8 8 0 016.707 2.707a8.001 8.001 0 1010.586 10.586z" />
    </svg>
  );

  // Settings icon
  const SettingsIcon = () => (
    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        strokeWidth={1.5}
        d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z"
      />
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        strokeWidth={1.5}
        d="M15 12a3 3 0 11-6 0 3 3 0 016 0z"
      />
    </svg>
  );

  // Sign out icon
  const SignOutIcon = () => (
    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        strokeWidth={1.5}
        d="M17 16l4-4m0 0l-4-4m4 4H7m6 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h4a3 3 0 013 3v1"
      />
    </svg>
  );

  // Campaigns icon
  const CampaignsIcon = () => (
    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        strokeWidth={1.5}
        d="M11 5.882V19.24a1.76 1.76 0 01-3.417.592l-2.147-6.15M18 13a3 3 0 100-6M5.436 13.683A4.001 4.001 0 017 6h1.832c4.1 0 7.625-1.234 9.168-3v14c-1.543-1.766-5.067-3-9.168-3H7a3.988 3.988 0 01-1.564-.317z"
      />
    </svg>
  );

  // Database icon
  const DatabaseIcon = () => (
    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        strokeWidth={1.5}
        d="M4 7v10c0 2.21 3.582 4 8 4s8-1.79 8-4V7M4 7c0 2.21 3.582 4 8 4s8-1.79 8-4M4 7c0-2.21 3.582-4 8-4s8 1.79 8 4m0 5c0 2.21-3.582 4-8 4s-8-1.79-8-4"
      />
    </svg>
  );

  // Resume Database icon
  const ResumeIcon = () => (
    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        strokeWidth={1.5}
        d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"
      />
    </svg>
  );

  // Navigate to Resume Database
  const handleNavigateToResumeDatabase = () => {
    navigate('/resume-parsing');
  };

  return (
    <header className="bg-white dark:bg-gray-900 border-b border-gray-200 dark:border-gray-800 sticky top-0 z-[80] transition-[background-color,border-color] duration-250 ease-out w-full">
      <div className="w-full mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex items-center justify-between h-14 py-2">
          {/* Left Section */}
          <div className="flex items-center gap-4">
            {/* App Menu (9-dot grid) */}
            <Popover
              position="bottom-left"
              trigger={
                <button
                  className="p-2 rounded-lg text-gray-400 hover:text-gray-600 dark:text-gray-500 dark:hover:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-800 transition-colors duration-250 opacity-80 hover:opacity-100"
                  aria-label="App menu"
                >
                  <GridIcon />
                </button>
              }
              contentClassName="w-64"
            >
              {({ close }) => (
                <>
                  <div className="p-2">
                    <PopoverMenuItem
                      icon={<CampaignsIcon />}
                      onClick={() => {
                        close();
                        handleNavigateToCampaigns();
                      }}
                    >
                      Campaigns
                    </PopoverMenuItem>
                    <PopoverMenuItem
                      icon={<DatabaseIcon />}
                      onClick={() => {
                        handleNavigateToDatabase();
                        // Navigate to master database when route is ready
                      }}
                    >
                      Master Database
                    </PopoverMenuItem>
                    <PopoverMenuItem
                      icon={<ResumeIcon />}
                      onClick={() => {
                        close();
                        handleNavigateToResumeDatabase();
                      }}
                    >
                      Resume Database
                    </PopoverMenuItem>
                    {/* <PopoverMenuItem
                      icon={
                        <svg className="w-5 h-5" fill="none" stroke="currentColor" strokeWidth="1.5" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" d="M10.5 6h9.75M10.5 6a1.5 1.5 0 11-3 0m3 0a1.5 1.5 0 10-3 0M3.75 6H7.5m3 12h9.75m-9.75 0a1.5 1.5 0 01-3 0m3 0a1.5 1.5 0 00-3 0m-3.75 0H7.5m9-6h3.75m-3.75 0a1.5 1.5 0 01-3 0m3 0a1.5 1.5 0 00-3 0m-9.75 0h9.75" />
                        </svg>
                      }
                      onClick={() => {
                        close();
                        navigate('/admin');
                      }}
                    >
                      Admin <span className="text-xs text-gray-500 dark:text-gray-400">(Development)</span>
                    </PopoverMenuItem> */}
                  </div>
                  <PopoverFooter className="flex items-center justify-center gap-4 text-xs">
                    <Link
                      to="/terms"
                      className="text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-300 transition-colors"
                    >
                      Terms of Service
                    </Link>
                    <span className="text-gray-300 dark:text-gray-600">•</span>
                    <Link
                      to="/privacy"
                      className="text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-300 transition-colors"
                    >
                      Privacy Policy
                    </Link>
                  </PopoverFooter>
                </>
              )}
            </Popover>

            {/* Logo and App Name */}
            <Link to="/dashboard" className="flex items-center gap-2 group">
              <img
                src={appLogo}
                alt={appName}
                className="w-8 h-8 object-contain transition-transform duration-250 group-hover:scale-105 flex-shrink-0"
                onError={(e) => {
                  e.target.style.display = 'none';
                }}
              />
              <span className="text-lg font-semibold text-gray-900 dark:text-white transition-colors duration-250">
                {appName}
              </span>
            </Link>

            {/* OpenAI API status warning — inline in topbar */}
            <OpenAIKeyWarning />
          </div>

          {/* Right Section */}
          <div className="flex items-center gap-1 sm:gap-2">
            {/* Theme Toggle */}
            <button
              onClick={toggleTheme}
              className="p-2 rounded-lg text-gray-400 hover:text-gray-600 dark:text-gray-500 dark:hover:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-800 transition-colors duration-250 opacity-80 hover:opacity-100 flex items-center justify-center"
              aria-label="Toggle theme"
            >
              {isDark ? <SunIcon /> : <MoonIcon />}
            </button>

            {/* Notifications Popover */}
            <Popover
              position="bottom-right"
              portal
              trigger={
                <button
                  className="p-2 rounded-lg text-gray-400 hover:text-gray-600 dark:text-gray-500 dark:hover:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-800 transition-colors duration-250 relative opacity-80 hover:opacity-100 flex items-center justify-center"
                  aria-label="Notifications"
                >
                  <BellIcon />
                  {notifCount > 0 && (
                    <span
                      className="absolute -top-0.5 -right-0.5 min-w-[18px] h-[18px] flex items-center justify-center text-[10px] font-bold text-white rounded-full px-1"
                      style={{ backgroundColor: '#EF4444' }}
                    >
                      {notifCount > 99 ? '99+' : notifCount}
                    </span>
                  )}
                </button>
              }
              contentClassName="w-[calc(100vw-1rem)] sm:w-[420px] max-h-[520px] overflow-hidden"
              onOpen={fetchNotifications}
            >
              {({ close }) => (
                <div className="flex flex-col max-h-[520px]">
                  {/* Popover Header */}
                  <div className="flex items-center justify-between px-4 py-3 border-b border-gray-200 dark:border-gray-700">
                    <div className="flex items-center gap-2">
                      <h3 className="text-sm font-semibold text-gray-900 dark:text-white">Notifications</h3>
                      {notifCount > 0 && (
                        <span
                          className="text-[10px] font-bold text-white rounded-full px-1.5 py-0.5"
                          style={{ backgroundColor: primaryColor }}
                        >
                          {notifCount}
                        </span>
                      )}
                    </div>
                    <div className="flex items-center gap-2">
                      {notifCount > 3 && (
                        <button
                          onClick={() => { close(); navigate('/notifications'); }}
                          className="text-[11px] font-medium text-blue-500 hover:text-blue-700 dark:text-blue-400 transition-colors"
                        >
                          View All ({notifCount})
                        </button>
                      )}
                      {notifications.length > 0 && (
                        <button
                          onClick={() => { handleClearAll(); close(); }}
                          className="text-[11px] font-medium text-gray-400 hover:text-red-500 dark:text-gray-500 dark:hover:text-red-400 transition-colors"
                        >
                          Clear All
                        </button>
                      )}
                      <button
                        onClick={close}
                        className="p-1 rounded-md text-gray-400 hover:text-gray-600 dark:text-gray-500 dark:hover:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors"
                        aria-label="Close notifications"
                      >
                        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                        </svg>
                      </button>
                    </div>
                  </div>

                  <div className="flex-1">
                    <NotificationSummaryList
                      notifications={notifications}
                      loading={notifLoading}
                      totalCount={notifCount}
                      primaryColor={primaryColor}
                      onOpenGroup={(group) => handleOpenNotificationGroup(group, close)}
                      onViewAll={() => { close(); navigate('/notifications'); }}
                    />
                    {notifCount > 0 && (
                      <div className="border-t border-gray-100 dark:border-gray-700 px-4 py-2.5 text-center">
                        <button
                          onClick={() => { close(); navigate('/notifications'); }}
                          className="text-xs font-medium hover:underline transition-colors"
                          style={{ color: primaryColor }}
                        >
                          View All Notifications →
                        </button>
                      </div>
                    )}
                  </div>
                </div>
              )}
            </Popover>

            {/* Divider */}
            <div className="w-px h-8 bg-gray-200 dark:bg-gray-700 mx-1 sm:mx-2 transition-colors duration-250" />

            {/* Account Menu */}
            <Popover
              position="bottom-right"
              trigger={
                <div className="flex items-center gap-2 cursor-pointer p-2 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-800 transition-colors duration-250">
                  {/* User Avatar */}
                  {user.profile ? (
                    <img
                      src={`${process.env.REACT_APP_API_URL?.replace('/api', '') || 'http://localhost:5000'}${user.profile}`}
                      alt={user.name}
                      className="w-9 h-9 rounded-full object-cover flex-shrink-0"
                      onError={(e) => {
                        // Fallback to initial on image load error
                        e.target.style.display = 'none';
                        e.target.nextSibling.style.display = 'flex';
                      }}
                    />
                  ) : null}
                  <div
                    className="w-9 h-9 rounded-full flex items-center justify-center text-white font-semibold text-sm flex-shrink-0"
                    style={{ 
                      backgroundColor: primaryColor,
                      display: user.profile ? 'none' : 'flex'
                    }}
                  >
                    {userInitial}
                  </div>
                  <div className="hidden sm:block">
                    <p className="text-sm font-medium text-gray-900 dark:text-white transition-colors duration-250">
                      {user.name || 'User'}
                    </p>
                    <p className="text-xs text-gray-500 dark:text-gray-400 transition-colors duration-250">
                      {user.role || 'HR'}
                    </p>
                  </div>
                  {/* Chevron */}
                  <svg
                    className="w-4 h-4 text-gray-400 hidden sm:block"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M19 9l-7 7-7-7"
                    />
                  </svg>
                </div>
              }
              contentClassName="w-56 min-w-[200px]"
            >
              {({ close }) => (
                <>
                  {/* User info in menu */}
                  <div className="px-4 py-3 border-b border-gray-200 dark:border-gray-700">
                    <p className="text-sm font-semibold text-gray-900 dark:text-white">
                      {user.name || 'User'}
                    </p>
                    <p className="text-xs text-gray-500 dark:text-gray-400 truncate">
                      {user.email || ''}
                    </p>
                  </div>
                  <div className="p-2">
                    <PopoverMenuItem
                      icon={<SettingsIcon />}
                      onClick={() => {
                        close();
                        handleNavigateToSettings();
                      }}
                    >
                      <span style={{display: 'flex', alignItems: 'center', gap: '8px', whiteSpace: 'nowrap'}}>Account & Settings</span>
                    </PopoverMenuItem>
                    <PopoverDivider />
                    <PopoverMenuItem
                      icon={<SignOutIcon />}
                      danger
                      onClick={() => {
                        close();
                        handleSignOut();
                      }}
                    >
                      Sign Out
                    </PopoverMenuItem>
                  </div>
                </>
              )}
            </Popover>
          </div>
        </div>
      </div>

      {/* Upcoming Interviews Modal */}
      <UpcomingInterviewsModal
        isOpen={upcomingModalOpen}
        onClose={() => setUpcomingModalOpen(false)}
        primaryColor={primaryColor}
        companyId={companyId}
        userId={currentUserId}
        searchId={currentSearchId}
        candidateEmail={currentCandidateEmail}
      />
    </header>
  );
};

export default AppHeader;
